import { XMarkIcon } from '@heroicons/react/24/outline';
import { ModalShell } from '../../shared/ModalShell';
import {
    useOrthopulseOrderPreview,
    useSendOrthopulseOrder
} from '../../../hooks/useOrthopulseOrder';
import { useToast } from '../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../utils/apiError';
import { AddressPill } from '../../shared/AddressPill';
import type { OrthopulseOrderPreview } from '../../../services/orthopulseOrder';
import { OrthopulseOrderNotice } from '../../shared/OrthopulseBlockedBanner';

function PreviewMeta({ preview }: { preview: OrthopulseOrderPreview }) {
    return (
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-3.5 flex flex-col gap-2.5 text-xs">
            <div className="flex items-center gap-4 flex-wrap">
                <span className="text-gray-500">
                    Vendor <span className="font-semibold text-gray-800">{preview.vendorName}</span>
                </span>
                <span className="text-gray-500">
                    PO <span className="font-mono text-gray-800">{preview.poNumberPreview}</span>
                </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-gray-500">From</span>
                <AddressPill address={preview.from} />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-gray-500">To</span>
                {preview.recipients.map((recipient) => (
                    <AddressPill key={recipient} address={recipient} />
                ))}
            </div>
        </div>
    );
}

function PreviewBody({ preview }: { preview: OrthopulseOrderPreview }) {
    return (
        <>
            <OrthopulseOrderNotice ordersVia={preview.ordersVia} blocked={preview.blocked} />
            <PreviewMeta preview={preview} />
            <div className="border border-gray-200 rounded-lg overflow-hidden h-[40vh] bg-gray-50">
                <iframe
                    title="Purchase order email preview"
                    srcDoc={preview.html}
                    sandbox=""
                    className="w-full h-full bg-white"
                />
            </div>
        </>
    );
}

interface SendPurchaseOrderModalProps {
    patientId: string;
    chargeId: string;
    onClose: () => void;
}

export function SendPurchaseOrderModal({
    patientId,
    chargeId,
    onClose
}: SendPurchaseOrderModalProps) {
    const { showToast } = useToast();
    const preview = useOrthopulseOrderPreview(patientId, chargeId);
    const sendOrder = useSendOrthopulseOrder(patientId, chargeId);

    const handleSend = async () => {
        try {
            const result = await sendOrder.mutateAsync(undefined);
            showToast(`Purchase order ${result.poNumber} sent.`, 'success');
            onClose();
        } catch (err) {
            showToast(apiErrorMessage(err, 'Failed to send purchase order'), 'error');
        }
    };

    return (
        <ModalShell
            widthClassName="max-w-[640px]"
            panelClassName="rounded-lg max-h-[88vh] flex flex-col overflow-hidden"
            dismissible={!sendOrder.isPending}
            onDismiss={onClose}
            labelledBy="po-send-title"
        >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
                <h2 id="po-send-title" className="text-lg font-bold text-gray-900">
                    Send purchase order
                </h2>
                <button
                    type="button"
                    onClick={onClose}
                    disabled={sendOrder.isPending}
                    className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-50"
                    aria-label="Close"
                >
                    <XMarkIcon className="h-5 w-5" />
                </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-6 flex flex-col gap-4">
                {preview.isLoading && (
                    <div className="flex items-center justify-center gap-2 text-gray-400 py-16">
                        <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                        <span className="text-sm">Loading preview…</span>
                    </div>
                )}

                {preview.isError && (
                    <p className="text-sm text-red-600">
                        {apiErrorMessage(preview.error, 'Failed to load the order preview')}
                    </p>
                )}

                {preview.data && <PreviewBody preview={preview.data} />}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 shrink-0">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={sendOrder.isPending}
                    className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50"
                >
                    Cancel
                </button>
                {preview.data && preview.data.blocked?.level !== 'error' && (
                    <button
                        type="button"
                        onClick={() => void handleSend()}
                        disabled={sendOrder.isPending}
                        className="bg-gray-900 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {sendOrder.isPending ? 'Sending…' : 'Send order'}
                    </button>
                )}
            </div>
        </ModalShell>
    );
}
